import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";

/**
 * Sticky mobile-only call-to-action bar shown at the bottom of public pages.
 * Helps users reach "Peça o seu" / WhatsApp without scrolling all the way down.
 *
 * Hidden on:
 *  - Desktop (>= lg)
 *  - The /contacto page (already a contact form)
 *  - The auth pages (have their own AuthHeader)
 *  - When user scrolls less than 320px (avoids hiding the hero CTAs)
 */
const HIDDEN_PATHS = ["/contacto", "/entrar", "/registar"];

export default function MobileStickyCta() {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 320);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const hidden = HIDDEN_PATHS.some((p) => pathname.startsWith(p));

    useEffect(() => {
        if (!hidden && visible) {
            document.body.setAttribute("data-sticky-cta", "true");
        } else {
            document.body.removeAttribute("data-sticky-cta");
        }
        return () => document.body.removeAttribute("data-sticky-cta");
    }, [hidden, visible]);

    if (hidden) return null;

    return (
        <div
            data-testid="mobile-sticky-cta"
            aria-hidden={!visible}
            className={
                "lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/95 backdrop-blur-md shadow-[0_-8px_24px_rgba(15,23,42,0.08)] transition-transform duration-300 " +
                (visible ? "translate-y-0" : "translate-y-full")
            }
            style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
            <div className="container-x flex items-center gap-2 py-2.5">
                <a
                    href="https://wa.me/351900000000?text=Ol%C3%A1%20NOVIFOOD%2C%20gostaria%20de%20saber%20mais"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-sticky-cta-whatsapp"
                    aria-label="Falar no WhatsApp"
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#25D366] text-white shadow-sm hover:brightness-95 transition"
                >
                    <MessageCircle className="h-5 w-5" />
                </a>
                <Link
                    to="/simulador"
                    data-testid="mobile-sticky-cta-simulador"
                    className="flex-1 inline-flex h-12 items-center justify-center gap-1.5 rounded-md border border-border bg-white px-3 text-sm font-bold text-primary hover:bg-muted transition"
                >
                    Simulador
                </Link>
                <Link
                    to="/contacto"
                    data-testid="mobile-sticky-cta-primary"
                    className="flex-1 inline-flex h-12 items-center justify-center gap-1.5 rounded-md bg-secondary px-3 text-sm font-bold text-primary shadow-sm hover:bg-yellow-300 transition"
                >
                    Peça o seu <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    );
}
